import type { Service } from "@/lib/types";
import { useLiveFeed } from "@/stores/liveFeed";
import { cn } from "@/lib/utils";
import { isIir, meterModelLabel } from "@/lib/meter";
import { useNow } from "./utils";
import type { ComtradeUrlStatus } from "./types";

/**
 * Per-row cell components for the AdminLoadIn roster table. Extracted from
 * routes/AdminLoadIn.tsx (2026-05-27).
 */

export function ModelCell({ systemId, staticModel }: { systemId: string; staticModel?: string | null }) {
  const live = useLiveFeed((s) => s.services[systemId]?.meter_model ?? null);
  const label = meterModelLabel(live ?? staticModel ?? null);
  return <span className="text-xs">{label ?? "—"}</span>;
}

export function LatencyCell({ systemId }: { systemId: string }) {
  const ts = useLiveFeed((s) => s.services[systemId]?.ts ?? null);
  const now = useNow(1000);
  if (ts == null) {
    return <span className="text-[10px] text-muted-foreground">offline</span>;
  }
  const ageS = Math.max(0, Math.round((now - ts) / 1000));
  const cls =
    ageS < 8 ? "text-emerald-400" :
    ageS < 60 ? "text-amber-400" : "text-red-500";
  const label = ageS < 60 ? `${ageS}s` : ageS < 3600 ? `${Math.round(ageS / 60)}m` : "stale";
  return <span className={cn("text-[10px] tabular-nums", cls)}>{label}</span>;
}

export function SystemCell({ s, isSynth }: { s: Service; isSynth: boolean }) {
  // Comtrade sync status is only meaningful on Acuvim 3 (II-R has no
  // datapost subsystem). Hide the badge on II-R meters so the column
  // reads cleanly — the new Model column is what tells the operator
  // it's a II-R.
  const liveModel = useLiveFeed((st) => st.services[s.system_id]?.meter_model ?? null);
  const model = liveModel ?? s.meter_model ?? null;
  return (
    <>
      {s.system_id}
      {isSynth && (
        <span className="ml-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-500 text-[10px] uppercase tracking-wide px-1.5 py-0.5">
          synthetic
        </span>
      )}
      {!isIir(model) && <ComtradeStatusBadge status={s.comtrade_url_status} />}
    </>
  );
}

export function ComtradeStatusBadge({ status }: { status: ComtradeUrlStatus | null | undefined }) {
  // "ok" and null/undefined render nothing — the absence of a badge is the
  // affirmative signal that the comtrade URL is in sync (or never needed sync).
  if (!status || status === "ok") return null;
  const styles: Record<ComtradeUrlStatus, { label: string; cls: string; title: string }> = {
    ok: { label: "", cls: "", title: "" },
    syncing: {
      label: "syncing…",
      cls: "border-blue-500/30 bg-blue-500/10 text-blue-400",
      title: "Comtrade post URL is being updated on the meter's cloud relay",
    },
    failed: {
      label: "sync failed",
      cls: "border-destructive/40 bg-destructive/10 text-destructive",
      title:
        "Last comtrade-URL sync to the meter's cloud relay failed. Check Slack alert; re-trigger with `docker exec cloud-api python3 -m cloud_acuvim_relay <system_id> <mac> <node_id>`",
    },
    no_relay_url: {
      label: "no relay URL",
      cls: "border-amber-500/40 bg-amber-500/10 text-amber-500",
      title: "This meter's MAC has no entry in /etc/ovr/secrets/acuvim_cloud_relays.json — add it",
    },
    unknown_node: {
      label: "node unsupported",
      cls: "border-amber-500/40 bg-amber-500/10 text-amber-500",
      title: "Node ID does not match the *.20N.1 gateway convention (e.g. node-10+ unsupported)",
    },
  };
  const s = styles[status];
  return (
    <span
      className={`ml-2 rounded-md border text-[10px] uppercase tracking-wide px-1.5 py-0.5 ${s.cls}`}
      title={s.title}
    >
      {s.label}
    </span>
  );
}
